import { Controller } from "stimulus";
import t from "../i18n";

export default class extends Controller {
  static targets = [
    "aiButton",
    "detailsPanel",
    "footerPreview",
    "helperText",
    "interestStatus",
    "medium",
    "messageEditor",
    "messagePanel",
    "modeButton",
    "nextContact",
    "nextStep",
    "progressBar",
    "progressPanel",
    "progressPercent",
    "responseEditor",
    "responsePanel",
    "sendEmail",
    "sendWarning",
    "statusDetail",
    "statusLabel",
    "statusPanel",
    "subject",
    "submitButton"
  ];

  connect() {
    this.leadId = this.element.dataset.contactComposerLeadId;
    this.mode = "message";
    this.busy = false;
    this.savedHandler = this.handleSaved.bind(this);
    this.errorHandler = this.handleExternalError.bind(this);

    document.addEventListener("contact-composer:saved", this.savedHandler);
    document.addEventListener("contact-composer:error", this.errorHandler);
    this.toggleSendEmail();
    this.sync();
  }

  disconnect() {
    document.removeEventListener("contact-composer:saved", this.savedHandler);
    document.removeEventListener("contact-composer:error", this.errorHandler);
    window.clearInterval(this.progressTimer);
  }

  setMode(event) {
    this.mode = event.currentTarget.dataset.mode;
    this.modeButtonTargets.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.mode === this.mode);
    });

    this.messagePanelTarget.classList.toggle("d-none", this.mode === "response");
    this.responsePanelTarget.classList.toggle("d-none", this.mode !== "response");
    this.detailsPanelTarget.classList.toggle("d-none", this.mode !== "details");

    if (this.mode === "response") {
      this.setStatus(t("contact_composer.status.capture_response", {}, "Antwort erfassen"), t("contact_composer.detail.capture_response", {}, "Die Nachricht vom Lead wird als Eingang im Verlauf gespeichert."));
      this.focusEditor(this.responseEditorTarget);
    } else if (this.mode === "details") {
      this.setStatus(t("contact_composer.status.adjust_details", {}, "Details feinjustieren"), t("contact_composer.detail.adjust_details", {}, "Kontaktart, Status und Follow-up sind bereit."));
    } else {
      this.setStatus(t("contact_composer.status.ready", {}, "Bereit"), t("contact_composer.detail.ready", {}, "Schreibe selbst, nutze Textbausteine oder lass die KI formulieren."));
      this.focusEditor(this.messageEditorTarget);
    }
  }

  sync() {
    const hasMessage = this.editorText(this.messageEditorTarget).trim().length > 0;
    const hasResponse = this.hasResponseEditorTarget && this.editorText(this.responseEditorTarget).trim().length > 0;

    if (!this.busy && (hasMessage || hasResponse)) {
      this.setStatus(t("contact_composer.status.draft_active", {}, "Entwurf aktiv"), t("contact_composer.detail.draft_active", {}, "Änderungen werden beim Speichern ohne Seiten-Refresh in den Verlauf übernommen."));
    }
  }

  insertPrompt(event) {
    const prompt = event.currentTarget.dataset.prompt;
    if (!prompt) return;

    this.ensureMessageMode();
    this.insertText(`${this.editorText(this.messageEditorTarget).trim() ? "\n\n" : ""}${prompt}`);
    this.setStatus(t("contact_composer.status.prompt_inserted", {}, "Prompt ergänzt"), t("contact_composer.detail.prompt_inserted", {}, "Starte jetzt die KI oder schreibe direkt weiter."));
  }

  insertTrigger(event) {
    const trigger = event.currentTarget.dataset.trigger;
    if (!trigger) return;

    this.ensureMessageMode();
    this.focusEditor(this.messageEditorTarget);
    this.messageEditorTarget.dispatchEvent(new CustomEvent("mentions:open", {
      bubbles: true,
      detail: { trigger }
    }));
    this.setStatus(
      trigger === "@" ? t("contact_composer.status.text_blocks_open", {}, "Textbausteine geöffnet") : t("contact_composer.status.lead_data_open", {}, "Lead-Daten geöffnet"),
      t("contact_composer.detail.pick_suggestion", {}, "Wähle einen Vorschlag direkt aus dem Menü aus.")
    );
  }

  insertTemplate(event) {
    const template = event.currentTarget.dataset.template;
    if (!template) return;

    this.ensureMessageMode();
    this.messageEditorTarget.editor.insertHTML(template);
    this.setStatus(t("contact_composer.status.template_inserted", {}, "Textbaustein eingefügt"), t("contact_composer.detail.template_inserted", {}, "Der Baustein liegt im Editor und kann weiter angepasst werden."));
  }

  async generateMessage(event) {
    event.preventDefault();
    if (this.busy) return;

    this.ensureMessageMode();
    const currentText = this.editorText(this.messageEditorTarget);
    const currentHtml = this.editorHTML(this.messageEditorTarget);
    this.setBusy(true);
    this.startProgress();
    this.setStatus(t("composer.status.ai_working", {}, "KI arbeitet"), t("contact_composer.detail.ai_working", {}, "Lead-Kontext, Verlauf und Entwurf werden analysiert."));

    try {
      const response = await fetch(`/leads/${this.leadId}/propose_next_message.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ current_text: currentText, current_html: currentHtml })
      });

      const data = await this.readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || t("composer.error.ai_response_load_failed", {}, "Die KI-Antwort konnte nicht geladen werden."));
      }
      if (data.error) {
        throw new Error(data.error);
      }

      const messageHTML = data.elements && data.elements[0] ? data.elements[0][1] : "";
      if (!messageHTML) {
        throw new Error(t("composer.error.ai_response_load_failed", {}, "Die KI-Antwort konnte nicht geladen werden."));
      }
      if (data.subject && this.hasSubjectTarget) {
        this.subjectTarget.value = data.subject;
      }

      this.setProgress(82, t("composer.progress.ai_writing", {}, "KI schreibt den Vorschlag live in den Editor."));
      await this.typeMessage(messageHTML);
      this.setProgress(100, t("composer.progress.suggestion_ready", {}, "Vorschlag ist bereit."));
      this.setStatus(t("contact_composer.status.ai_suggestion_ready", {}, "KI-Vorschlag fertig"), t("contact_composer.detail.ai_suggestion_ready", {}, "Prüfe Betreff und Nachricht, dann speichern oder senden."));
    } catch (error) {
      this.setStatus(t("composer.status.ai_failed", {}, "KI konnte nicht schreiben"), error.message || t("common.try_again", {}, "Bitte versuche es erneut."));
      this.setProgress(0, t("composer.progress.ai_error", {}, "Fehler bei der KI-Verarbeitung."));
    } finally {
      window.setTimeout(() => {
        this.setBusy(false);
        this.progressPanelTarget.classList.add("d-none");
      }, 450);
    }
  }

  toggleSendEmail() {
    if (!this.hasSendEmailTarget) return;

    const isSending = this.sendEmailTarget.checked && !this.sendEmailTarget.disabled;
    if (this.hasFooterPreviewTarget) {
      this.footerPreviewTarget.classList.toggle("d-none", !isSending);
    }
    if (this.hasSendWarningTarget) {
      this.sendWarningTarget.classList.toggle("d-none", !isSending);
    }
    if (isSending) {
      this.setStatus(t("contact_composer.status.sending_enabled", {}, "Versand aktiviert"), t("contact_composer.detail.sending_enabled", {}, "Beim Speichern wird die E-Mail direkt verschickt."));
    }
  }

  submitStarted() {
    this.setStatus(t("contact_composer.status.saving", {}, "Speichert"), t("contact_composer.detail.saving", {}, "Der Kontaktpunkt wird gesichert und gleich im Verlauf angezeigt."));
    this.setSubmitDisabled(true);
  }

  submitError() {
    this.setSubmitDisabled(false);
    this.setStatus(t("contact_composer.status.save_failed", {}, "Speichern fehlgeschlagen"), t("contact_composer.detail.save_failed", {}, "Bitte prüfe die Eingaben und versuche es erneut."));
  }

  handleSaved(event) {
    if (String(event.detail.leadId) !== String(this.leadId)) return;
    if (!this.element.id.includes("new")) return;

    this.resetComposer();
    this.setStatus(t("contact_composer.status.saved", {}, "Gespeichert"), event.detail.message || t("contact_composer.detail.saved", {}, "Der neue Kontaktpunkt ist im Verlauf sichtbar."));
  }

  handleExternalError(event) {
    if (String(event.detail.leadId) !== String(this.leadId)) return;

    this.setSubmitDisabled(false);
    this.setStatus(t("common.error", {}, "Fehler"), event.detail.message || t("contact_composer.detail.external_error", {}, "Der Kontaktpunkt konnte nicht gespeichert werden."));
  }

  ensureMessageMode() {
    if (this.mode === "message") return;

    const messageButton = this.modeButtonTargets.find((button) => button.dataset.mode === "message");
    if (messageButton) messageButton.click();
  }

  async typeMessage(html) {
    const editor = this.messageEditorTarget.editor;
    const text = this.plainTextFromHtml(html);
    const chunks = text.match(/.{1,10}(\s|$)|\S+/g) || [text];
    let visibleText = "";

    editor.loadHTML("");
    for (const chunk of chunks) {
      visibleText += chunk;
      editor.loadHTML(this.previewHtmlFromText(visibleText));
      await this.sleep(14);
    }

    if (html) {
      editor.loadHTML(this.normalizeEmailHtml(html));
    }
  }

  insertText(text) {
    this.focusEditor(this.messageEditorTarget);
    this.messageEditorTarget.editor.insertString(text);
  }

  focusEditor(editorTarget) {
    if (editorTarget && typeof editorTarget.focus === "function") {
      editorTarget.focus();
    }
  }

  editorText(editorTarget) {
    if (!editorTarget || !editorTarget.editor) return "";
    return editorTarget.editor.getDocument().toString();
  }

  editorHTML(editorTarget) {
    if (!editorTarget) return "";
    return editorTarget.innerHTML || "";
  }

  plainTextFromHtml(html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html || "";
    return wrapper.innerText.trim();
  }

  previewHtmlFromText(text) {
    const escaped = this.escapeHtml(text || "");
    const paragraphs = escaped
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.replace(/\n/g, "<br>"))
      .filter((paragraph) => paragraph.trim().length > 0);

    return paragraphs.length ? paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join("") : "";
  }

  normalizeEmailHtml(html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html || "";

    if (!wrapper.querySelector("p, ul, ol, blockquote, h1, h2, h3, h4, div, br")) {
      return this.previewHtmlFromText(wrapper.textContent || "");
    }

    return wrapper.innerHTML;
  }

  escapeHtml(value) {
    const wrapper = document.createElement("div");
    wrapper.textContent = value;
    return wrapper.innerHTML;
  }

  resetComposer() {
    if (this.hasMessageEditorTarget) this.messageEditorTarget.editor.loadHTML("");
    if (this.hasResponseEditorTarget) this.responseEditorTarget.editor.loadHTML("");
    if (this.hasSubjectTarget) this.subjectTarget.value = "";
    if (this.hasSendEmailTarget) this.sendEmailTarget.checked = false;
    this.toggleSendEmail();
    this.setSubmitDisabled(false);
  }

  startProgress() {
    window.clearInterval(this.progressTimer);
    this.progressPanelTarget.classList.remove("d-none");
    this.setProgress(8, t("composer.progress.reading_context", {}, "Lead-Kontext wird gelesen."));

    const steps = [
      [28, t("composer.progress.summarizing_contacts", {}, "Letzte Kontaktpunkte werden verdichtet.")],
      [46, t("composer.progress.prioritizing_value", {}, "Nutzenargumente werden priorisiert.")],
      [63, t("composer.progress.tuning_subject_tone", {}, "Betreff und Tonalität werden abgestimmt.")],
      [74, t("composer.progress.preparing_text", {}, "Der Text wird vorbereitet.")]
    ];
    let index = 0;

    this.progressTimer = window.setInterval(() => {
      if (index >= steps.length) {
        window.clearInterval(this.progressTimer);
        return;
      }
      this.setProgress(steps[index][0], steps[index][1]);
      index += 1;
    }, 650);
  }

  setProgress(percent, detail) {
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percent}%`;
    if (this.hasProgressPercentTarget) this.progressPercentTarget.textContent = `${percent}%`;
    if (this.hasStatusDetailTarget) this.statusDetailTarget.textContent = detail;
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.element.classList.toggle("is-busy", isBusy);
    if (this.hasAiButtonTarget) this.aiButtonTarget.disabled = isBusy;
    this.setSubmitDisabled(isBusy);
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = disabled;
      this.submitButtonTarget.classList.toggle("is-loading", disabled);
    }
  }

  setStatus(label, detail) {
    if (this.hasStatusLabelTarget) this.statusLabelTarget.textContent = label;
    if (this.hasHelperTextTarget && detail) this.helperTextTarget.textContent = detail;
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']");
    return token ? token.content : "";
  }

  async readJsonResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      return {};
    }
  }

  sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }
}
